/**
 * Zombie / skeleton spawn variants (Bedrock normal difficulty):
 *  - baby (zombie, husk, drowned only): 5%, speed x1.5, half size.
 *  - armour: 15% of undead spawn armoured; tier from leather -> gold -> iron -> diamond
 *    (each step 9.5%, up to 3 times), pieces helmet -> boots, each next piece 75%.
 *    Armour points reduce damage (points / 25), each piece drops 8.5% when killed by the player.
 *  - zombies hold an iron sword or shovel 1% of the time (+ its melee damage).
 * The renderer does not draw mob armour yet; `view.armor` carries the item ids for it.
 */

import { I } from '../../game/itemIds';
import { stackOf, type Stack } from '../../game/types';
import type { Mob } from '../mob';

export const BABY_ZOMBIE_CHANCE = 0.05;
export const UNDEAD_ARMOR_CHANCE = 0.15;

/** [helmet, chestplate, leggings, boots] per tier, with armour points. */
const TIERS: readonly { ids: readonly number[]; pts: readonly number[] }[] = [
  { ids: [I.LEATHER_HELMET, I.LEATHER_CHESTPLATE, I.LEATHER_LEGGINGS, I.LEATHER_BOOTS], pts: [1, 3, 2, 1] },
  { ids: [I.GOLDEN_HELMET, I.GOLDEN_CHESTPLATE, I.GOLDEN_LEGGINGS, I.GOLDEN_BOOTS], pts: [2, 5, 3, 1] },
  { ids: [I.IRON_HELMET, I.IRON_CHESTPLATE, I.IRON_LEGGINGS, I.IRON_BOOTS], pts: [2, 6, 5, 2] },
  { ids: [I.DIAMOND_HELMET, I.DIAMOND_CHESTPLATE, I.DIAMOND_LEGGINGS, I.DIAMOND_BOOTS], pts: [3, 8, 6, 3] },
];

/** Roll armour for a spawn: item ids helmet..boots (0 = empty) or null for none. */
export function rollArmor(rnd: () => number): number[] | null {
  if (rnd() >= UNDEAD_ARMOR_CHANCE) return null;
  let tier = 0;
  for (let i = 0; i < 3; i++) if (rnd() < 0.095) tier++;
  const out = [0, 0, 0, 0];
  for (let slot = 0; slot < 4; slot++) {
    out[slot] = TIERS[tier].ids[slot];
    if (rnd() >= 0.75) break;
  }
  return out;
}

export function armorPoints(pieces: readonly number[]): number {
  let n = 0;
  for (let slot = 0; slot < pieces.length; slot++) {
    for (const t of TIERS) if (pieces[slot] && t.ids[slot] === pieces[slot]) n += t.pts[slot];
  }
  return n;
}

export function armorDrops(pieces: readonly number[], rnd: () => number): Stack[] {
  const out: Stack[] = [];
  for (const id of pieces) if (id && rnd() < 0.085) out.push(stackOf(id, 1));
  return out;
}

/** Give a freshly built undead its armour (and zombies a tool). `melee` receives the bonus damage. */
export function applyUndeadVariants(m: Mob, rnd: () => number, zombie: boolean): void {
  const a = rollArmor(rnd);
  if (a) m.armorPieces = a;
  if (zombie && rnd() < 0.01) {
    m.heldItem = rnd() < 0.5 ? I.IRON_SWORD : I.IRON_SHOVEL;
    m.meleeBonus = m.heldItem === I.IRON_SWORD ? 6 : 3.5;
  }
}

/** Baby zombie: half size, 1.5x speed. */
export function makeBaby(m: Mob): void {
  const self = m as unknown as { width: number; height: number; eyeHeight: number; speed: number };
  self.width *= 0.5; self.height *= 0.5; self.eyeHeight *= 0.5; self.speed *= 1.5;
  m.baby = true;
  m.view.baby = true;
}
